import type { ScheduleComponent } from '@syncfusion/ej2-react-schedule'
import { type RefObject, useCallback, useEffect, useRef } from 'react'
import { SCHEDULE_COLUMN_WIDTH_PX, toReferenceColumnWidth } from './helpers'

const HANDLE_CLASS = 'schedule-column-resize-handle'
const LAYER_CLASS = 'schedule-column-resize-layer'
const HANDLE_ACTIVE_CLASS = 'is-active'
const RESIZING_CLASS = 'schedule-column-resizing'
const HANDLE_WIDTH_PX = 10

type AttachArgs = {
  scheduleElement: HTMLElement
  columnKeys: string[]
  interval: number
  maxConcurrentByColumn: Map<string, number>
  isWeekView: boolean
  onResizeEnd: (columnKey: string, referenceWidthPx: number) => void
}

const getColumnWidthPx = (scheduleElement: HTMLElement, index: number): number => {
  const cell = getColumnCells(scheduleElement)[index]
  if (cell) {
    const width = cell.getBoundingClientRect().width
    if (width > 0) return width
  }

  const contentCol = scheduleElement.querySelectorAll<HTMLTableColElement>(
    '.e-vertical-view .e-content-wrap table col',
  )[index]
  if (!contentCol) return 0
  const parsed = Number.parseFloat(contentCol.style.width || '')
  if (Number.isFinite(parsed) && parsed > 0) return parsed
  return contentCol.getBoundingClientRect().width
}

const setColumnWidthPx = (scheduleElement: HTMLElement, index: number, widthPx: number): void => {
  const width = `${Math.round(widthPx)}px`
  const headerCol = scheduleElement.querySelectorAll<HTMLTableColElement>(
    '.e-vertical-view .e-date-header-wrap table col',
  )[index]
  const contentCol = scheduleElement.querySelectorAll<HTMLTableColElement>(
    '.e-vertical-view .e-content-wrap table col',
  )[index]
  if (headerCol) headerCol.style.width = width
  if (contentCol) contentCol.style.width = width
}

const getColumnCells = (scheduleElement: HTMLElement, isWeekView = false): HTMLElement[] => {
  const headerSelector = isWeekView
    ? '.e-vertical-view .e-date-header-wrap td.e-header-cells'
    : '.e-vertical-view .e-date-header-wrap td.e-resource-cells'
  const headerCells = Array.from(scheduleElement.querySelectorAll<HTMLElement>(headerSelector))
  if (headerCells.length > 0) return headerCells

  const firstRow = scheduleElement.querySelector('.e-vertical-view .e-content-wrap tbody tr')
  if (!firstRow) return []
  return Array.from(firstRow.querySelectorAll<HTMLElement>('td.e-work-cells'))
}

const ensureLayer = (parent: HTMLElement): HTMLElement => {
  let layer = parent.querySelector<HTMLElement>(`:scope > .${LAYER_CLASS}`)
  if (!layer) {
    layer = document.createElement('div')
    layer.className = LAYER_CLASS
    parent.appendChild(layer)
  }
  return layer
}

/**
 * One continuous handle per column, snapped to the rendered cell's right border.
 */
const syncUnifiedHandles = ({
  layer,
  handles,
  verticalView,
  headerWrap,
  contentWrap,
  scheduleElement,
  columnCount,
  isWeekView,
}: {
  layer: HTMLElement
  handles: HTMLElement[]
  verticalView: HTMLElement
  headerWrap: HTMLElement | null
  contentWrap: HTMLElement
  scheduleElement: HTMLElement
  columnCount: number
  isWeekView: boolean
}) => {
  const parentRect = verticalView.getBoundingClientRect()
  const contentRect = contentWrap.getBoundingClientRect()
  const headerRect = headerWrap?.getBoundingClientRect()
  const contentTable = contentWrap.querySelector<HTMLElement>('table')
  const tableRect = contentTable?.getBoundingClientRect()

  const top = (headerRect?.top ?? contentRect.top) - parentRect.top
  const gridBottom = tableRect ? Math.min(contentRect.bottom, tableRect.bottom) : contentRect.bottom
  const bottom = gridBottom - parentRect.top
  const headerHeight = headerRect?.height ?? 32

  layer.style.top = '0'
  layer.style.left = '0'
  layer.style.width = '100%'
  layer.style.height = '100%'

  const cells = getColumnCells(scheduleElement, isWeekView)

  handles.forEach((handle) => {
    const index = Number(handle.dataset.columnIndex)
    if (!Number.isFinite(index) || index < 0 || index >= columnCount) return

    const cell = cells[index]
    if (!cell) {
      handle.style.display = 'none'
      return
    }

    const cellRect = cell.getBoundingClientRect()
    // Snap to the cell's right border, centered on the handle hit area.
    const left = cellRect.right - parentRect.left - HANDLE_WIDTH_PX / 2

    handle.style.display = ''
    handle.style.top = `${top}px`
    handle.style.height = `${Math.max(0, bottom - top)}px`
    handle.style.left = `${left}px`
    handle.style.setProperty('--schedule-resize-header-height', `${headerHeight}px`)
  })
}

/**
 * Attaches a single full-height resize handle on each column border (header through body).
 * Returns a cleanup function.
 */
export const attachScheduleColumnResizeHandles = ({
  scheduleElement,
  columnKeys,
  interval,
  maxConcurrentByColumn,
  isWeekView,
  onResizeEnd,
}: AttachArgs): (() => void) => {
  if (columnKeys.length === 0) return () => {}

  const verticalView = scheduleElement.querySelector<HTMLElement>('.e-vertical-view')
  const headerWrap = scheduleElement.querySelector<HTMLElement>(
    '.e-vertical-view .e-date-header-wrap',
  )
  const contentWrap = scheduleElement.querySelector<HTMLElement>('.e-vertical-view .e-content-wrap')
  if (!verticalView || !contentWrap) return () => {}

  const computedPosition = window.getComputedStyle(verticalView).position
  if (computedPosition === 'static') {
    verticalView.style.position = 'relative'
  }

  const layer = ensureLayer(verticalView)
  layer.replaceChildren()

  const handles: HTMLElement[] = []
  const cleanups: Array<() => void> = []
  let activeColumnIndex: number | null = null

  const setHandleActive = (columnIndex: number | null) => {
    activeColumnIndex = columnIndex
    handles.forEach((handle) => {
      const index = Number(handle.dataset.columnIndex)
      handle.classList.toggle(HANDLE_ACTIVE_CLASS, columnIndex != null && index === columnIndex)
    })
  }

  const refresh = () => {
    syncUnifiedHandles({
      layer,
      handles,
      verticalView,
      headerWrap,
      contentWrap,
      scheduleElement,
      columnCount: columnKeys.length,
      isWeekView,
    })
  }

  columnKeys.forEach((columnKey, index) => {
    const handle = document.createElement('div')
    handle.className = HANDLE_CLASS
    handle.dataset.columnIndex = String(index)
    handle.setAttribute('role', 'separator')
    handle.setAttribute('aria-orientation', 'vertical')
    handle.setAttribute('aria-label', 'Resize column')
    handle.title = 'Drag to resize'
    layer.appendChild(handle)
    handles.push(handle)

    const onPointerEnter = () => {
      if (document.body.classList.contains(RESIZING_CLASS)) return
      setHandleActive(index)
    }

    const onPointerLeave = () => {
      if (document.body.classList.contains(RESIZING_CLASS)) return
      if (activeColumnIndex === index) setHandleActive(null)
    }

    const onPointerDown = (event: PointerEvent) => {
      event.preventDefault()
      event.stopPropagation()

      const startX = event.clientX
      const startWidth = getColumnWidthPx(scheduleElement, index)
      if (startWidth <= 0) return

      const concurrent = Math.max(1, Math.floor(maxConcurrentByColumn.get(columnKey) ?? 1) || 1)
      document.body.classList.add(RESIZING_CLASS)
      setHandleActive(index)
      handle.setPointerCapture(event.pointerId)

      const onPointerMove = (moveEvent: PointerEvent) => {
        const delta = moveEvent.clientX - startX
        const minWidth = concurrent * SCHEDULE_COLUMN_WIDTH_PX.minUnit
        const maxWidth = concurrent * SCHEDULE_COLUMN_WIDTH_PX.maxUnit
        const nextWidth = Math.min(maxWidth, Math.max(minWidth, startWidth + delta))
        setColumnWidthPx(scheduleElement, index, nextWidth)
        refresh()
      }

      const onPointerUp = (upEvent: PointerEvent) => {
        handle.releasePointerCapture(upEvent.pointerId)
        document.body.classList.remove(RESIZING_CLASS)
        window.removeEventListener('pointermove', onPointerMove)
        window.removeEventListener('pointerup', onPointerUp)

        const finalWidth = getColumnWidthPx(scheduleElement, index)
        const unitWidth = finalWidth / concurrent
        onResizeEnd(columnKey, toReferenceColumnWidth(unitWidth, interval))

        const stillOver = handle.matches(':hover')
        setHandleActive(stillOver ? index : null)
      }

      window.addEventListener('pointermove', onPointerMove)
      window.addEventListener('pointerup', onPointerUp)
    }

    handle.addEventListener('pointerenter', onPointerEnter)
    handle.addEventListener('pointerleave', onPointerLeave)
    handle.addEventListener('pointerdown', onPointerDown)
    cleanups.push(() => {
      handle.removeEventListener('pointerenter', onPointerEnter)
      handle.removeEventListener('pointerleave', onPointerLeave)
      handle.removeEventListener('pointerdown', onPointerDown)
    })
  })

  const onScroll = () => refresh()
  contentWrap.addEventListener('scroll', onScroll, { passive: true })
  headerWrap?.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', refresh)
  cleanups.push(() => {
    contentWrap.removeEventListener('scroll', onScroll)
    headerWrap?.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', refresh)
  })

  refresh()

  return () => {
    document.body.classList.remove(RESIZING_CLASS)
    cleanups.forEach((cleanup) => cleanup())
    layer.remove()
  }
}

type UseScheduleColumnResizeArgs = {
  scheduleObj: RefObject<ScheduleComponent | null>
  onResizeEnd: (columnKey: string, referenceWidthPx: number) => void
}

/**
 * Holds the latest resize callback and cleans up handles on unmount.
 * Call `reattach` from column-width layout after Syncfusion paints.
 */
export const useScheduleColumnResize = ({
  scheduleObj,
  onResizeEnd,
}: UseScheduleColumnResizeArgs) => {
  const onResizeEndRef = useRef(onResizeEnd)
  onResizeEndRef.current = onResizeEnd
  const cleanupRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => {
      cleanupRef.current?.()
      cleanupRef.current = null
    }
  }, [scheduleObj])

  const reattachColumnResizeHandles = useCallback(
    ({
      columnKeys,
      interval,
      maxConcurrentByColumn,
      isWeekView,
    }: {
      columnKeys: string[]
      interval: number
      maxConcurrentByColumn: Map<string, number>
      isWeekView: boolean
    }) => {
      const schedule = scheduleObj.current
      if (!schedule?.element) return

      const root = schedule as unknown as { isDestroyed?: boolean }
      if (root.isDestroyed) return

      cleanupRef.current?.()
      cleanupRef.current = attachScheduleColumnResizeHandles({
        scheduleElement: schedule.element,
        columnKeys,
        interval,
        maxConcurrentByColumn,
        isWeekView,
        onResizeEnd: (columnKey, referenceWidthPx) => {
          onResizeEndRef.current(columnKey, referenceWidthPx)
        },
      })
    },
    [scheduleObj],
  )

  return { reattachColumnResizeHandles }
}
